import { useState } from "react";
import SeoHead from "../components/SeoHead";
import { getSiteContent, localeRoutes } from "../i18n/siteContent";

function ContactsPage({ locale = "en" }) {
    const site = getSiteContent(locale);
    const text = site.contact;
    const routes = localeRoutes[locale];
    const breadcrumbItems = [
        { name: site.nav.home, path: routes.home },
        { name: site.nav.contact, path: routes.contact }
    ];

    const [formState, setFormState] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
        company: ""
    });

    const apiUrl = process.env.REACT_APP_ESTATE2026_API_URL;

    const onChange = (event) => {
        const { name, value } = event.target;
        setFormState((prev) => ({ ...prev, [name]: value }));
    };

    const toMailto = () => {
        const subject = `${text.heading} - ${formState.name || "New contact"}`;
        const body = [
            `Name: ${formState.name || "-"}`,
            `Email: ${formState.email || "-"}`,
            `Phone: ${formState.phone || "-"}`,
            "",
            "Request:",
            formState.message || "-"
        ].join("\n");

        return `mailto:info@mauriziobrioschi.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    const onSubmit = async (event) => {
        event.preventDefault();

        if (!apiUrl) {
            window.location.href = toMailto();
            return;
        }

        try {
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formState)
            });

            if (!response.ok) {
                throw new Error("contact submit failed");
            }

            setFormState({ name: "", email: "", phone: "", message: "", company: "" });
        } catch (error) {
            window.location.href = toMailto();
        }
    };

    return (
        <>
            <SeoHead
                title={text.title}
                description={text.description}
                keywords="contact Maurizio Brioschi, book a strategy call, fractional cto contact, solution architect consultant contact, digital transformation consultant"
                path={routes.contact}
                locale={locale}
                enPath={localeRoutes.en.contact}
                itPath={localeRoutes.it.contact}
                breadcrumbItems={breadcrumbItems}
            />
            <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
                <header>
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">{text.label}</p>
                    <h1 className="mt-3 text-4xl font-impact text-slate-900">{text.heading}</h1>
                    <p className="mt-4 max-w-3xl text-slate-600">{text.intro}</p>
                </header>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <form className="grid gap-4" onSubmit={onSubmit}>
                        <input type="text" name="company" value={formState.company} onChange={onChange} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                        <div className="grid gap-4 md:grid-cols-2">
                            <label className="grid gap-2 text-sm font-medium text-slate-800">
                                {text.fields.name}
                                <input name="name" value={formState.name} onChange={onChange} required className="rounded-xl border border-slate-300 px-4 py-3 text-sm" />
                            </label>
                            <label className="grid gap-2 text-sm font-medium text-slate-800">
                                {text.fields.email}
                                <input type="email" name="email" value={formState.email} onChange={onChange} required className="rounded-xl border border-slate-300 px-4 py-3 text-sm" />
                            </label>
                        </div>
                        <label className="grid gap-2 text-sm font-medium text-slate-800">
                            {text.fields.phone}
                            <input name="phone" value={formState.phone} onChange={onChange} className="rounded-xl border border-slate-300 px-4 py-3 text-sm" />
                        </label>
                        <label className="grid gap-2 text-sm font-medium text-slate-800">
                            {text.fields.message}
                            <textarea name="message" value={formState.message} onChange={onChange} rows={5} required className="rounded-xl border border-slate-300 px-4 py-3 text-sm" />
                        </label>
                        <button type="submit" className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-700">
                            {text.cta}
                        </button>
                    </form>

                    <div className="mt-5 flex flex-wrap gap-3 text-sm">
                        <a href="mailto:info@mauriziobrioschi.com" className="rounded-full border border-slate-300 px-4 py-2 font-semibold text-slate-800">info@mauriziobrioschi.com</a>
                        <a href="tel:+393515699197" className="rounded-full border border-slate-300 px-4 py-2 font-semibold text-slate-800">+39 351 569 9197</a>
                    </div>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.internalLinksTitle}</h2>
                    <nav className="mt-4 grid gap-3 sm:grid-cols-2" aria-label={text.internalLinksTitle}>
                        {text.internalLinks.map((item) => (
                            <a
                                key={item.href}
                                href={item.href}
                                className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-amber-300 hover:text-amber-700"
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>
                </section>
            </main>
        </>
    );
}

export default ContactsPage;
